import React from 'react';
import { cn } from '@/lib/utils';
import { Container } from '@/components/layout/Container';

/**
 * Editorial section wrapper: generous vertical rhythm, optional tone.
 * tones: canvas | canvas2 | surface | ink
 */
export const Section = ({
  id,
  tone = 'canvas',
  className,
  innerClassName,
  wide = false,
  bare = false,
  children,
  ...rest
}) => {
  const tones = {
    canvas: 'bg-[color:var(--rmh-canvas)] text-[color:var(--rmh-ink)]',
    canvas2: 'bg-[color:var(--rmh-canvas-2)] text-[color:var(--rmh-ink)]',
    surface: 'bg-[color:var(--rmh-surface)] text-[color:var(--rmh-ink)]',
    ink: 'bg-[color:var(--rmh-ink)] text-[color:var(--rmh-canvas)]',
  };

  return (
    <section
      id={id}
      className={cn('relative py-16 sm:py-20 lg:py-28', tones[tone], className)}
      {...rest}
    >
      {bare ? children : <Container wide={wide} className={innerClassName}>{children}</Container>}
    </section>
  );
};

/** Eyebrow + serif heading + optional lede, used across every section. */
export const SectionHeading = ({
  eyebrow,
  title,
  lede,
  align = 'left',
  invert = false,
  className,
  titleClassName,
  children,
}) => (
  <div
    className={cn(
      'flex flex-col',
      align === 'center' ? 'items-center text-center' : 'items-start',
      className,
    )}
  >
    {eyebrow ? (
      <div className="flex items-center gap-3">
        <span
          className="block h-px w-8"
          style={{ backgroundColor: 'var(--rmh-accent)' }}
          aria-hidden="true"
        />
        <span
          className="rmh-eyebrow"
          style={invert ? { color: 'rgba(250,247,242,0.68)' } : undefined}
        >
          {eyebrow}
        </span>
      </div>
    ) : null}
    {title ? (
      <h2
        className={cn(
          'rmh-h2 mt-5 text-[1.75rem] sm:text-[2.1rem] lg:text-[2.6rem] max-w-3xl',
          titleClassName,
        )}
        style={invert ? { color: 'var(--rmh-canvas)' } : undefined}
      >
        {title}
      </h2>
    ) : null}
    {lede ? (
      <p
        className={cn(
          'rmh-body mt-5 max-w-2xl text-[0.95rem] sm:text-base',
          align === 'center' && 'mx-auto',
        )}
        style={invert ? { color: 'rgba(250,247,242,0.74)' } : undefined}
      >
        {lede}
      </p>
    ) : null}
    {children}
  </div>
);

export default Section;
